import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { TypeAnimation } from "react-type-animation";
import { Link } from "react-router-dom";
import { ASSETS, BUSINESS, GALLERY } from "../lib/assets";
import SectionHeading from "../components/SectionHeading";
import { FlyingPigSVG } from "../components/FlyingPig";
import MenuCardWithLightbox from "../components/MenuCardWithLightbox";
import usePageMeta from "../hooks/usePageMeta";
import { HolidayCtaButton, HolidaySection } from "../components/Holiday";
import { ArrowUpRight, Flame, MapPin, Utensils, Clock, Crown, Award, Star } from "lucide-react";

const retailers = ["WALMART", "PUBLIX", "WINN-DIXIE", "HEB", "SAM'S CLUB", "COSTCO", "TARGET", "SPROUTS"];

export default function Home() {
  usePageMeta({
    title: "Real BBQ · Made the Florida Man Way",
    description: "Award-winning slow-smoked brisket, ribs, pulled pork & chicken from Spring Hill, FL. Family-owned BBQ serving Pasco, Hernando, Citrus, Northern Pinellas and Hillsborough counties.",
    path: "/",
  });
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], ["0%", "20%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.35], [1, 0]);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] w-full overflow-hidden border-b border-ink" data-testid="hero-section">
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
          <img src={ASSETS.pitmasterSmoke} alt="Pitmaster with smoke" className="mono-img h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/60" />
          <div className="grain-block absolute inset-0" />
        </motion.div>

        <div className="relative z-10 mx-auto grid min-h-[92vh] max-w-[1400px] grid-cols-1 items-end gap-8 px-4 pb-16 pt-32 md:grid-cols-12 md:px-8">
          <div className="md:col-span-8">
            <div className="mb-6 inline-flex items-center gap-2 border border-bone px-3 py-1 font-mono text-[10px] uppercase tracking-[0.3em] text-bone">
              <Flame size={12} /> Spring Hill · Real BBQ · Made the Florida Man Way
            </div>

            <h1 className="font-rye text-5xl uppercase leading-[0.9] tracking-tight text-bone sm:text-6xl lg:text-8xl" data-testid="hero-title">
              When Pigs<br />Fly.
            </h1>

            <div className="mt-6 min-h-[56px] max-w-2xl font-typewriter text-lg leading-snug text-bone caret sm:text-2xl" data-testid="hero-typewriter">
              <TypeAnimation
                sequence={[
                  "Slow-smoked brisket that took 14 hours.",
                  2000,
                  "Ribs that fall off the bone. Not off the plate.",
                  2000,
                  "Pulled pork made low & slow for your family.",
                  2000,
                  "From our family to yours — Spring Hill, FL.",
                  2500,
                ]}
                speed={55}
                repeat={Infinity}
                cursor={false}
              />
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={BUSINESS.toastOrderUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-order-btn"
                className="border-2 border-bone bg-bone px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-ink transition-colors duration-200 hover:bg-transparent hover:text-bone"
              >
                Order Online →
              </a>
              <Link
                to="/catering"
                data-testid="hero-catering-btn"
                className="border-2 border-bone bg-transparent px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink"
              >
                Book Catering
              </Link>
              <HolidayCtaButton testid="hero-holiday-btn" />
            </div>

            <div className="mt-14 grid max-w-lg grid-cols-3 gap-4 border-t border-bone/40 pt-6 font-mono text-[10px] uppercase tracking-[0.24em] text-bone/80">
              <div><Utensils size={14} className="mb-2" />Family Recipes</div>
              <div><MapPin size={14} className="mb-2" />Spring Hill FL</div>
              <div><Clock size={14} className="mb-2" />14 hr Smoke</div>
            </div>
          </div>

          <div className="hidden md:col-span-4 md:block">
            <div className="ml-auto max-w-xs border-l-2 border-bone pl-6">
              <div className="eyebrow mb-2 text-bone/60">Serving</div>
              <p className="font-alfa text-3xl uppercase leading-none text-bone">
                Pasco · Hernando · Citrus · Pinellas · Hillsborough
              </p>
              <p className="mt-3 font-serif text-sm text-bone/70">
                Nature Coast BBQ that travels. Catering weddings, corporate events & tailgates across Florida.
              </p>
            </div>
          </div>
        </div>

        {/* Scroll cue */}
      </section>

      {/* MARQUEE — words enter from far right and scroll left continuously (no edge fade, seamless loop) */}
      <section className="relative overflow-hidden border-b border-ink bg-ink py-4" aria-hidden data-testid="marquee-section">
        <div
          className="flex whitespace-nowrap animate-marquee gap-16 will-change-transform"
          style={{ animationDelay: "-2s", paddingLeft: "100vw" }}
        >
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex shrink-0 items-center gap-16 pr-16">
              {["Brisket", "Ribs", "Pulled Pork", "Smoked Chicken", "Sausage", "Burnt Ends", "Cornbread Casserole", "Slaw", "Beans"].map((w) => (
                <span key={w + i} className="font-alfa text-3xl uppercase text-bone tracking-tight">{w} <span className="text-bone/40">✕</span></span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* HOW TO EAT — 3 ordering channels */}
      <section className="border-b border-ink bg-white" data-testid="ordering-strip">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 divide-y divide-ink border-x-0 sm:grid-cols-2 sm:divide-y-0 sm:divide-x md:grid-cols-3 lg:grid-cols-5">
          {[
            { t: "Dine-In", d: "Come sit down at the pit.", href: "/locations", testid: "channel-dinein" },
            { t: "Takeout", d: "Order ahead. Pick up hot.", href: BUSINESS.toastOrderUrl, external: true, testid: "channel-takeout" },
            { t: "DoorDash", d: "Delivered to your door.", href: BUSINESS.doordashUrl, external: true, testid: "channel-doordash" },
            { t: "Uber Eats", d: "First order: code eats-14b3pehxhe.", href: BUSINESS.ubereatsUrl, external: true, testid: "channel-ubereats" },
            { t: "Catering", d: "Weddings & big events.", href: "/catering", testid: "channel-catering" },
          ].map((c, i) => {
            const Body = (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <div className="eyebrow text-ink/50">0{i + 1}</div>
                  <FlyingPigSVG className="piglet-badge h-10 w-14" />
                </div>
                <div className="font-alfa text-2xl uppercase leading-none md:text-3xl">{c.t}</div>
                <p className="mt-2 font-serif text-sm text-ink/70 group-hover:text-bone/80">{c.d}</p>
                <span className="mt-4 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.24em] link-underline">Learn more →</span>
              </>
            );
            const cls = "group block bg-white p-8 transition-colors duration-200 hover:bg-ink hover:text-bone";
            return c.external ? (
              <a key={c.t} href={c.href} target="_blank" rel="noreferrer" className={cls} data-testid={c.testid}>{Body}</a>
            ) : (
              <Link key={c.t} to={c.href} className={cls} data-testid={c.testid}>{Body}</Link>
            );
          })}
        </div>
      </section>

      <HolidaySection />

      {/* SMALL TOWN SHOWDOWN — banner CTA */}
      <section className="border-b border-ink bg-ink py-14 md:py-20" data-testid="register-event-banner">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-4 md:grid-cols-12 md:px-8">
          <div className="mx-auto max-w-sm md:col-span-4 md:mx-0">
            <Link to="/small-town-showdown" data-testid="home-showdown-logo-link" className="block transition-transform duration-300 hover:scale-[1.03]">
              <img
                src="/small-town-showdown-logo.png"
                alt="Small Town Showdown BBQ Competition"
                className="w-full drop-shadow-2xl"
                data-testid="home-showdown-logo"
              />
            </Link>
          </div>
          <div className="md:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-bone/60">Live registration open</p>
            <h2 className="mt-3 font-alfa text-3xl uppercase leading-tight text-bone md:text-5xl">
              Small Town Showdown
            </h2>
            <p className="mt-4 max-w-2xl font-serif text-base text-bone/85 md:text-lg">
              BBQ Competition — <b>March 11th–14th, 2027</b>. Load-in Thursday 1:00 PM at
              15362 Alric Pottberg Rd, Shady Hills, FL. Grab your team's spot before the pit fills up.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/small-town-showdown"
                data-testid="home-register-cta"
                className="border-2 border-bone bg-bone px-8 py-4 font-alfa text-lg uppercase tracking-[0.2em] text-ink transition-colors hover:bg-transparent hover:text-bone"
              >
                Sign My Team Up
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION / INTRO */}
      <section className="relative border-b border-ink py-24 md:py-32" data-testid="mission-section">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-5">
            <SectionHeading
              eyebrow="From Our Family to Yours"
              title={"Family Owned.\nOne Pit.\nA Whole Lotta Smoke."}
              subtitle="What started as a backyard smoker is now a family operation serving Spring Hill and the Nature Coast. Every rack, every brisket, every side is made the way we'd make it for our own family."
            />
          </div>
          <div className="md:col-span-7">
            <div className="grain-block relative w-full overflow-hidden border-2 border-ink shadow-hard" data-testid="mission-video">
              <div className="relative aspect-video w-full">
                <iframe
                  src="https://www.youtube.com/embed/ZMAGTJbEfo0?rel=0&modestbranding=1"
                  title="When Pigs Fly Family BBQ — Our Story"
                  className="absolute inset-0 h-full w-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
            <div className="mt-8 border border-ink bg-bone p-6 shadow-hard">
              <p className="font-serif text-lg italic leading-relaxed">
                &ldquo;Great barbecue feeds the body. Family feeds the soul.&rdquo;
              </p>
              <p className="mt-3 eyebrow text-ink/70">— The When Pigs Fly Family</p>
            </div>
          </div>
        </div>
      </section>

      {/* MENU HIGHLIGHTS — the full menu card */}
      <section className="relative border-b border-ink bg-ink py-24 md:py-32" data-testid="menu-highlights-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <div className="eyebrow mb-3 text-bone/60">Off the pit</div>
              <h2 className="font-alfa text-4xl uppercase leading-none tracking-tight text-bone sm:text-5xl lg:text-6xl">
                What&rsquo;s Smoking
              </h2>
              <div className="mt-6 h-[2px] w-24 bg-bone" />
              <p className="mt-6 max-w-xl font-serif text-base leading-relaxed text-bone/80">
                Slow. Steady. Serious BBQ. Every platter smoked daily on-site — served with your choice of two sides.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href={BUSINESS.toastOrderUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border-2 border-bone bg-bone px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-200 hover:bg-transparent hover:text-bone"
                data-testid="menu-highlight-order-btn"
              >
                Order Online <ArrowUpRight size={16} />
              </a>
              <Link
                to="/menu"
                className="inline-flex items-center gap-2 border-2 border-bone bg-transparent px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink"
                data-testid="menu-highlight-fullmenu-btn"
              >
                See Full Menu →
              </Link>
            </div>
          </div>

          <div data-testid="home-menu-card" className="space-y-4">
            <MenuCardWithLightbox testid="home-menu-card-btn" />
            <MenuCardWithLightbox
              src={ASSETS.menuCardBack}
              alt="When Pigs Fly Family BBQ — Sandwiches, By The Pound, Lunch Special, Kids Menu"
              testid="home-menu-card-back-btn"
            />
          </div>
        </div>
      </section>

      {/* RETAILERS */}
      <section className="relative border-b border-ink bg-ink py-20 text-bone" data-testid="retailers-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div className="mb-10 grid grid-cols-1 items-end gap-6 md:grid-cols-12">
            <div className="md:col-span-8">
              <div className="eyebrow mb-3 text-bone/60">Now stocked at</div>
              <h2 className="font-alfa text-4xl uppercase leading-none tracking-tight text-bone sm:text-5xl lg:text-6xl">
                Available at Walmart<br />&amp; Other Major Retailers
              </h2>
            </div>
            <div className="md:col-span-4">
              <p className="font-serif text-sm leading-relaxed text-bone/80">
                Our signature rubs and sauces are now on shelves at Walmart and select grocers across Florida. Bring the pit home.
              </p>
              <Link to="/seasonings" data-testid="retailers-shop-btn" className="mt-4 inline-block border border-bone px-4 py-2 font-mono text-[10px] uppercase tracking-[0.24em] transition-colors duration-200 hover:bg-bone hover:text-ink">
                Shop the seasonings →
              </Link>
            </div>
          </div>

          <div className="marquee-mask overflow-hidden border-y border-bone/30 py-8">
            <div className="flex animate-marquee gap-14 whitespace-nowrap">
              {Array.from({ length: 2 }).flatMap((_, i) => retailers.map((r, j) => (
                <span key={`${r}-${i}-${j}`} className="font-rye text-4xl uppercase text-bone md:text-5xl">
                  {r}
                </span>
              )))}
            </div>
          </div>
        </div>
      </section>

      {/* CATERING CTA */}
      <section className="relative border-b border-ink py-24 md:py-32" data-testid="catering-cta-section">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-12 px-4 md:grid-cols-12 md:px-8">
          <div className="md:col-span-6">
            <div className="grain-block relative aspect-[4/5] w-full overflow-hidden border border-ink shadow-hard">
              <img src={ASSETS.cateringSpread} alt="Full catering spread with chafing dishes" className="mono-img h-full w-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-6 md:pl-6">
            <div className="eyebrow mb-3 text-ink/70">Weddings · Corporate · Family Reunions</div>
            <h2 className="font-alfa text-4xl uppercase leading-none tracking-tight sm:text-5xl lg:text-6xl">
              Book The Pit<br />For Your Event
            </h2>
            <p className="mt-6 max-w-xl font-serif text-base leading-relaxed text-ink/80">
              We cater 50 to 500 guests across Pasco, Hernando, Citrus, Northern Pinellas and Hillsborough counties. Full-service setup,
              professional crew, and a custom menu built around your day. Interactive quote in under a minute.
            </p>
            <ul className="mt-8 divide-y divide-ink/30 border-y border-ink/30">
              {["Full-service catering", "Slow-smoked meats", "Homemade sides & fixings", "Professional setup & service", "Custom menus for any budget"].map((f) => (
                <li key={f} className="flex items-center gap-3 py-3 font-mono text-xs uppercase tracking-[0.18em]">
                  <span className="inline-block h-2 w-2 bg-ink" /> {f}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/catering" data-testid="home-cta-quote-btn" className="border-2 border-ink bg-ink px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink">
                Request a Quote
              </Link>
              <Link to="/gallery" data-testid="home-cta-gallery-btn" className="border-2 border-ink px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] transition-colors duration-200 hover:bg-ink hover:text-bone">
                See The Gallery
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="border-b border-ink bg-bone py-24" data-testid="home-gallery-section">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <SectionHeading eyebrow="From the pit" title="Recent Cooks" />
          <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
            {GALLERY.slice(0, 8).map((g, i) => (
              <div key={i} className="grain-block relative aspect-square overflow-hidden border border-ink">
                <img src={g.src} alt={g.alt} className="mono-img h-full w-full object-cover transition-transform duration-500 hover:scale-110" />
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/gallery" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.24em] link-underline" data-testid="home-view-gallery">
              View full gallery <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* TROPHY WALL — awards summary + top wins */}
      <section className="border-b border-ink bg-ink py-24 text-bone" data-testid="home-trophy-wall">
        <div className="mx-auto max-w-[1400px] px-4 md:px-8">
          <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-5">
              <div className="eyebrow mb-3 text-bone/60">Championship record</div>
              <h2 className="font-alfa text-4xl uppercase leading-none tracking-tight sm:text-5xl lg:text-6xl">
                Trophies On<br />The Wall
              </h2>
              <p className="mt-6 max-w-md font-serif text-base leading-relaxed text-bone/80">
                Real recipes, cooked at real competitions, judged blind against the country&rsquo;s best pit crews.
                Every jar of rub on this site is the same recipe that put these trophies on the wall.
              </p>
              <div className="mt-10 grid grid-cols-3 gap-4 border-y border-bone/20 py-8">
                <div data-testid="stat-total-awards">
                  <div className="font-alfa text-4xl uppercase leading-none sm:text-5xl">30</div>
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/60">Total placings</div>
                </div>
                <div data-testid="stat-first-places">
                  <div className="font-alfa text-4xl uppercase leading-none sm:text-5xl">6</div>
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/60">1st-place finishes</div>
                </div>
                <div data-testid="stat-championships">
                  <div className="font-alfa text-4xl uppercase leading-none sm:text-5xl">7</div>
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/60">Championships</div>
                </div>
              </div>
              <Link
                to="/events"
                data-testid="home-view-awards"
                className="mt-10 inline-flex items-center gap-2 border-2 border-bone px-6 py-4 font-mono text-xs uppercase tracking-[0.24em] text-bone transition-colors duration-200 hover:bg-bone hover:text-ink"
              >
                See The Full Trophy Wall <ArrowUpRight size={14} />
              </Link>
            </div>

            <div className="md:col-span-7">
              <div className="eyebrow mb-6 text-bone/60">Top wins · 2024 – 2026</div>
              <ul className="divide-y divide-bone/20 border-y border-bone/20">
                {[
                  { year: "2026", label: "Kids Q Grand Champion — Jace (Torry Island)", Icon: Crown },
                  { year: "2026", label: "Kids Q Grand Champion — Berkley (Jeep for Justice)", Icon: Crown },
                  { year: "2026", label: "Exotic Champion — Quail (Torry Island)", Icon: Star },
                  { year: "2025", label: "Grand Champion — Jimmy Jam", Icon: Crown },
                  { year: "2025", label: "Grand Champion — Torry Island", Icon: Crown },
                  { year: "2025", label: "Reserve Grand Champion — Haines City", Icon: Award },
                  { year: "2025", label: "Exotic Champion — Alligator (Torry Island)", Icon: Star },
                ].map(({ year, label, Icon }, i) => (
                  <li key={i} className="flex items-center gap-4 py-4">
                    <Icon size={22} className="shrink-0 text-bone" strokeWidth={1.5} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/50 tabular-nums">
                      {year}
                    </span>
                    <span className="flex-1 font-serif text-sm leading-tight sm:text-base">
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
